// src/pages/orders/components/PaymentRecordsSection.jsx
import React, { useState } from 'react';
import {
  X,
  CreditCard,
  DollarSign,
  FileText,
  Calendar,
  AlertCircle,
  Upload,
  Paperclip,
  CheckCircle2
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { calculatePaymentBalance, formatCurrency } from '../../../utils/orderCalculations';
import { uploadOrderPaymentProof } from '../../../services/api/orders';
import { getImageUrl } from '../../../utils/imageUtils';
import { PaymentMethodIcon } from '../../../utils/paymentMethodIcons';
import PaymentMethodPicker from '../../../components/ui/PaymentMethodPicker/PaymentMethodPicker';
import { getLocalTodayYMD, clampToToday } from '../../../utils/dateUtils';
import styles from './PaymentRecordsSection.module.css';

const VALID_PROOF_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_PROOF_SIZE = 5 * 1024 * 1024; // 5MB
const CASH_PAYMENT_METHOD_KEY = 'cash';
const isPdfUrl =(url) => !!url && url.toLowerCase().endsWith('.pdf');

const PaymentRecordsSection = ({
  paymentRecords,
  paymentMethods,
  orderTotal,
  errors,
  required = true,
  totalLabel,
  onAddRecord,
  onUpdateRecord,
  onRemoveRecord
}) => {
  const { t } = useLocalization();
  const [uploadingId, setUploadingId] = useState(null);
  const [proofErrors, setProofErrors] = useState({});

  const paymentBalance = calculatePaymentBalance(orderTotal, paymentRecords);
  const cashPaid = paymentRecords
    .filter(record => record.paymentMethodKey === CASH_PAYMENT_METHOD_KEY)
    .reduce((sum, record) => sum + (parseFloat(record.paymentAmount) || 0), 0);
  const codToCollect = Math.max(0, (parseFloat(orderTotal) || 0) - (paymentBalance.totalPaid - cashPaid));

  const handleProofChange = async (recordId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!VALID_PROOF_TYPES.includes(file.type)) {
      setProofErrors(prev => ({ ...prev, [recordId]: t('ecomOrders.invalidProofFormat') || 'Only .jpg, .png or .pdf files are allowed' }));
      return;
    }
    if (file.size > MAX_PROOF_SIZE) {
      setProofErrors(prev => ({ ...prev, [recordId]: t('ecomOrders.proofTooLarge') || 'File must be under 5MB' }));
      return;
    }

    setProofErrors(prev => ({ ...prev, [recordId]: '' }));
    setUploadingId(recordId);
    try {
      const proofUrl = await uploadOrderPaymentProof(file);
      onUpdateRecord(recordId, 'proofUrl', proofUrl);
    } catch (err) {
      console.error('Payment proof upload failed:', err);
      setProofErrors(prev => ({ ...prev, [recordId]: t('ecomOrders.proofUploadFailed') || 'Upload failed, please try again' }));
    } finally {
      setUploadingId(null);
    }
  };

  const renderPaymentRecord = (record, index) => (
    <div key={record.id} className={styles.paymentRecord}>
      <div className={styles.recordHeader}>
        <div className={styles.recordTitle}>
          <PaymentMethodIcon
            iconFileName={paymentMethods.find(m => m.key === record.paymentMethodKey)?.iconFileName}
            label={paymentMethods.find(m => m.key === record.paymentMethodKey)?.label}
          />
          {t('ecomOrders.paymentRecordNumber', { number: index + 1 })}
        </div>
        {record.isVerified ? (
          <span className={styles.verifiedRecordIcon} title={t('ecomOrders.verifiedPaymentRecord') || 'Verified'}>
            <CheckCircle2 size={20} />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onRemoveRecord(record.id)}
            className={styles.removeRecordButton}
            title={t('ecomOrders.removePaymentRecord')}
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className={styles.recordFields}>
        <div className={styles.fieldRow}>
          <div className={styles.formGroup}>
            <label>
              {t('ecomOrders.paymentAmount')} *
            </label>
            <div className={styles.inputWrapper}>
              <DollarSign className={styles.inputIcon} size={16} />
              <input
                type="number"
                value={record.paymentAmount}
                onChange={(e) => onUpdateRecord(record.id, 'paymentAmount', e.target.value)}
                step="0.01"
                min="0"
                max={orderTotal}
                placeholder="0.00"
                className={errors.paymentRecordDetails?.[index]?.paymentAmount ? styles.error : ''}
                disabled={record.isVerified}
                required
              />
            </div>
            {errors.paymentRecordDetails?.[index]?.paymentAmount && (
              <span className={styles.errorText}>
                {errors.paymentRecordDetails[index].paymentAmount}
              </span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label>
              {t('ecomOrders.paymentDate')} *
            </label>
            <div className={styles.inputWrapper}>
              <Calendar className={styles.inputIcon} size={16} />
              <input
                type="date"
                value={record.paymentDate}
                onChange={(e) => onUpdateRecord(record.id, 'paymentDate', clampToToday(e.target.value))}
                max={getLocalTodayYMD()}
                className={errors.paymentRecordDetails?.[index]?.paymentDate ? styles.error : ''}
                disabled={record.isVerified}
                required
              />
            </div>
            {errors.paymentRecordDetails?.[index]?.paymentDate && (
              <span className={styles.errorText}>
                {errors.paymentRecordDetails[index].paymentDate}
              </span>
            )}
          </div>
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.formGroup}>
            <label>
              {t('ecomOrders.paymentReference')}
            </label>
            <div className={styles.inputWrapper}>
              <FileText className={styles.inputIcon} size={16} />
              <input
                type="text"
                value={record.reference}
                onChange={(e) => onUpdateRecord(record.id, 'reference', e.target.value)}
                placeholder={t('ecomOrders.paymentReferencePlaceholder')}
                disabled={record.isVerified}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>{t('ecomOrders.paymentProof') || 'Payment Proof'}</label>
            {record.proofUrl ? (
              <div className={styles.proofPreview}>
                {isPdfUrl(record.proofUrl) ? (
                  <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofFileLink}>
                    <Paperclip size={16} />
                    {t('ecomOrders.viewProofPdf') || 'View uploaded PDF'}
                  </a>
                ) : (
                  <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofThumbnailLink}>
                    <img src={getImageUrl(record.proofUrl)} alt="Payment proof" className={styles.proofThumbnail} />
                  </a>
                )}
                {!record.isVerified && (
                  <button
                    type="button"
                    className={styles.changeProofButton}
                    onClick={() => document.getElementById(`proofInput-${record.id}`).click()}
                  >
                    {t('ecomOrders.changeProof') || 'Change'}
                  </button>
                )}
              </div>
            ) : (
              !record.isVerified && (
                <button
                  type="button"
                  className={styles.uploadProofButton}
                  onClick={() => document.getElementById(`proofInput-${record.id}`).click()}
                  disabled={uploadingId === record.id}
                >
                  <Upload size={14} />
                  {uploadingId === record.id ? (t('common.uploading') || 'Uploading...') : (t('ecomOrders.uploadProof') || 'Upload proof')}
                </button>
              )
            )}
            {!record.isVerified && (
              <input
                id={`proofInput-${record.id}`}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => handleProofChange(record.id, e)}
                style={{ display: 'none' }}
              />
            )}
            {proofErrors[record.id] && <span className={styles.errorText}>{proofErrors[record.id]}</span>}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.paymentRecordsContent}>
      <div className={styles.recordsHeader}>
        <h3>{t('ecomOrders.paymentMethod')}</h3>
      </div>
      <PaymentMethodPicker
        methods={paymentMethods}
        onSelect={(key) => onAddRecord(key)}
        disabled={paymentBalance.isFullyPaid}
        getTitle={(method) => paymentBalance.isFullyPaid
          ? (t('ecomOrders.noRemainingBalance') || 'No remaining balance to add a payment record')
          : method.label}
      />

      <div className={styles.recordsHeader}>
        <h3>{t('ecomOrders.paymentRecords')}</h3>
      </div>

      {paymentRecords.length > 0 ? (
        <div className={styles.recordsList}>
          {paymentRecords.map((record, index) => renderPaymentRecord(record, index))}
        </div>
      ) : (
        <div className={styles.emptyRecords}>
          <CreditCard size={48} />
          <p>{required ? t('ecomOrders.paymentRecordsRequired') : (t('ecomOrders.paymentRecordsOptional') || 'Payment records are optional for this payment type')}</p>
        </div>
      )}

      {errors.paymentRecords && (
        <div className={styles.sectionError}>
          <AlertCircle size={16} />
          {errors.paymentRecords}
        </div>
      )}

      {/* Payment Summary */}
      {paymentRecords.length > 0 && (
        <div className={styles.paymentSummary}>
          <div className={styles.summaryHeader}>
            <h4>{t('ecomOrders.paymentSummary')}</h4>
          </div>
          
          <div className={styles.summaryItems}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>{totalLabel || t('ecomOrders.orderTotal')}:</span>
              <span className={styles.summaryValue}>
                {formatCurrency(orderTotal)}
              </span>
            </div>
            
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>
                {t('ecomOrders.totalPaid')}:
              </span>
              <span className={styles.summaryValue}>
                {formatCurrency(paymentBalance.totalPaid)}
              </span>
            </div>
            
            <div className={`${styles.summaryItem} ${styles.balanceItem}`}>
              <span className={styles.summaryLabel}>
                {paymentBalance.isOverpaid 
                  ? t('ecomOrders.overpaidAmount')
                  : t('ecomOrders.paymentBalance')
                }:
              </span>
              <span className={`${styles.summaryValue} ${
                paymentBalance.isFullyPaid 
                  ? paymentBalance.isOverpaid 
                    ? styles.overpaid 
                    : styles.paid
                  : styles.pending
              }`}>
                {formatCurrency(paymentBalance.balance)}
              </span>
            </div>

            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>
                {t('ecomOrders.codToCollect')}:
              </span>
              <span className={styles.summaryValue}>
                {formatCurrency(codToCollect)}
              </span>
            </div>
          </div>

          <div className={styles.paymentStatus}>
            <div className={`${styles.statusBadge} ${
              paymentBalance.isFullyPaid 
                ? paymentBalance.isOverpaid 
                  ? styles.overpaidBadge 
                  : styles.paidBadge
                : styles.pendingBadge
            }`}>
              {paymentBalance.isFullyPaid 
                ? paymentBalance.isOverpaid 
                  ? t('ecomOrders.paymentOverpaid')
                  : t('ecomOrders.paymentFullyPaid')
                : t('ecomOrders.paymentPending')
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRecordsSection;